import { Directive, Output, EventEmitter, HostListener, Input, ElementRef, Renderer2 } from '@angular/core';
import { FileValidationService, FileValidationOptions, DragValidationResult } from '../services/file-validation.service';
import { DragDropVisualUtils } from '../utilities/drag-drop.utilities';
import { TranslateService } from '@ngx-translate/core';

@Directive({
    selector: '[appDragDrop]',
    standalone: false
})
export class DragDropDirective {
  @Output() dropHandler: EventEmitter<DragEvent> = new EventEmitter<DragEvent>();
  @Input() allowedTypes?: string[];
  @Input() maxFiles?: number;

  public dragging: boolean;
  private currentValidation: DragValidationResult = { isValid: true };

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private fileValidationService: FileValidationService,
    private translateService: TranslateService
  ) {}

  @HostListener('dragover', ['$event']) onDragOver(event: DragEvent) {
    event.preventDefault();

    if (event.dataTransfer) {
      this.validateAndUpdateState(event.dataTransfer);
      event.dataTransfer.dropEffect = this.currentValidation.isValid ? 'copy' : 'none';
    }

    return false;
  }

  @HostListener('dragenter', ['$event']) handleDragEnter(event: DragEvent) {
    this.dragging = true;

    if (event.dataTransfer) {
      this.validateAndUpdateState(event.dataTransfer);
    }
  }

  @HostListener('dragleave') handleDragLeave() {
    this.dragging = false;
    this.resetValidationState();
    this.resetDragVisualState();
  }

  @HostListener('drop', ['$event']) handleDrop(ev: any) {
    ev.preventDefault();
    this.dragging = false;

    this.handleInputChange(ev);
    this.resetValidationState();
    this.resetDragVisualState();
  }

  handleInputChange(ev: any) {
    this.dropHandler.emit(ev);
  }

  private validateAndUpdateState(dataTransfer: DataTransfer): void {
    const validationOptions: FileValidationOptions = {
      allowedTypes: this.allowedTypes,
      maxFiles: this.maxFiles,
      showErrors: false
    };

    const dataToValidate = dataTransfer.items || dataTransfer.files;
    this.currentValidation = this.fileValidationService.validateDragFiles(
      dataToValidate,
      validationOptions
    );

    this.updateDragVisualState();
  }

  private resetValidationState(): void {
    this.currentValidation = { isValid: true };
  }

  private updateDragVisualState(): void {
    const element = this.elementRef.nativeElement;
    DragDropVisualUtils.updateElementClassesWithMessage(
      element,
      this.renderer,
      this.currentValidation,
      this.translateService
    );
  }

  private resetDragVisualState(): void {
    const element = this.elementRef.nativeElement;
    DragDropVisualUtils.resetElementClasses(element, this.renderer);
  }
}
